import * as React from 'react';

/** Material UI Packages. */
import { Typography, Box, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { CustomAutocompleteCell, CustomAutocompleteCell_TradePartner, DateTypingEditCell } from 'shared/ui/RenderCell';
import { dispute_type, dispute_disputeType, dispute_actionTaken, dispute_statusMsg } from 'shared/config/constantsData';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import { parsePastedDate } from 'shared/lib/datagridHelpers';

/** Define Dispute Datagrid Fields. */
export const getColumns = (
    tabValue,
    handleDisputeDelete,
    row,
    hblList,
    cntrList,
    aliasList,
    tradePartnerOptions,
    handleTradePartnerChange
) => {
    return [
        {
            field: 'id',
            headerName: 'ID',
            align: 'center',
            headerAlign: 'center',
            width: 30,
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
            hide: true,
        },
        {
            field: 'dispute_date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            headerName: 'Occurrence',
            flex: 0.5,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // valueGetter: (params) => {
            //     const date = params;
            //     return date ? new Date(date) : null;
            // },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            valueFormatter: (params) => {
                if (params) {
                    const date = parseDateToLocal(params);
                    if (date) {
                        return date.toLocaleDateString('en-US');
                    }
                }
                return params;
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'dispute_of',
            headerName: 'Type',
            align: 'center',
            headerAlign: 'center',
            flex: 0.5,
            editable: true,
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {typeof params.value === 'string'
                            ? params.value
                            : dispute_type.find((option) => option.id === params.value)?.content || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                const matchedType = dispute_type.filter((item) => item.type.includes(tabValue));
                const value = matchedType.find((option) => option.id === params.value);
                return (
                    <CustomAutocompleteCell params={params} options={matchedType} value={value} shouldFocus={true} />
                );
            },
        },
        {
            field: 'dispute_of_name',
            headerName: 'Number',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {params.value || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                const getMatchedOptions = (params) => {
                    const { dispute_of } = params.row;
                    switch (dispute_of) {
                        case 1:
                            return row.mbl ? [row.mbl] : [];
                        case 2:
                        case 6:
                            return hblList
                                .filter(
                                    (item) =>
                                        item.il_ocean_import_id == row.id ||
                                        item.il_ocean_export_id == row.id ||
                                        item.il_air_import_id == row.id ||
                                        item.il_air_export_id == row.id
                                )
                                .map((item) => item.hbl || item.hawb)
                                .filter(Boolean);
                        case 3:
                        case 7:
                            const hbl_id = hblList
                                .filter(
                                    (item) =>
                                        item.il_ocean_import_id === row.id ||
                                        item.il_ocean_export_id === row.id ||
                                        item.il_air_import_id === row.id ||
                                        item.il_air_export_id === row.id
                                )
                                .map((item) => item.id);
                            return cntrList
                                .filter((item) =>
                                    hbl_id.includes(
                                        item.il_ocean_import_hbl_id ||
                                            item.il_ocean_export_hbl_id ||
                                            item.il_air_import_hbl_id ||
                                            item.il_air_export_hbl_id
                                    )
                                )
                                .map((item) => item.cntr_number || item.alias)
                                .filter(Boolean);
                        case 4:
                            const hbl_id_case4 = hblList
                                .filter((item) => item.il_ocean_import_id === row.id)
                                .map((item) => item.id);
                            const cntr_id = cntrList
                                .filter((item) => hbl_id_case4.includes(item.il_ocean_import_hbl_id))
                                .map((item) => item.id);
                            return aliasList
                                .filter((item) => cntr_id.includes(item.il_ocean_import_cntr_id))
                                .map((item) => item.alias_name)
                                .filter(Boolean);
                        case 5:
                            return row.mawb ? [row.mawb] : [];
                        case 8:
                            return row.bol ? [row.bol] : [];
                        case 9:
                            return hblList
                                .filter((item) => item.il_ftl_ltl_id == row.id)
                                .map((item) => item.alias)
                                .filter(Boolean);
                        default:
                            return [];
                    }
                };

                return (
                    <CustomAutocompleteCell
                        params={params}
                        options={getMatchedOptions(params)}
                        shouldFocus={true}
                        useCustomComparisons={false}
                    />
                );
            },
        },
        {
            field: 'dispute_type',
            headerName: 'Operational Stage',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {typeof params.value === 'string'
                            ? params.value
                            : dispute_disputeType.find((option) => option.id === params.value)?.content || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                const value = dispute_disputeType.find((option) => option.id === params.value);
                return (
                    <CustomAutocompleteCell
                        params={params}
                        options={dispute_disputeType}
                        value={value}
                        shouldFocus={true}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id',
            headerName: 'Disputed Party',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {params.value.name}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'dispute_original_rate',
            headerName: 'Original Rate',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            type: 'number',
            renderCell: (params) => (
                <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                    {params.value ? `$${Number(params.value).toLocaleString()}` : ''}
                </Typography>
            ),
        },
        {
            field: 'dispute_requested_rate',
            headerName: 'Requested Rate',
            flex: 0.7,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            type: 'number',
            renderCell: (params) => (
                <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                    {params.value ? `$${Number(params.value).toLocaleString()}` : ''}
                </Typography>
            ),
        },
        {
            field: 'dispute_reason',
            headerName: 'Reason',
            editable: true,
            flex: 1.7,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                const limitedText = params.value?.length > 100 ? `${params.value.slice(0, 70)}...` : params.value;
                return (
                    <Box sx={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                        <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                            {limitedText}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'dispute_action',
            headerName: 'Action Taken',
            editable: true,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {typeof params.value === 'string'
                            ? params.value
                            : dispute_actionTaken.find((option) => option.id === params.value)?.content || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                const value = dispute_actionTaken.find((option) => option.id === params.value);
                return (
                    <CustomAutocompleteCell
                        params={params}
                        options={dispute_actionTaken}
                        value={value}
                        shouldFocus={true}
                    />
                );
            },
        },
        {
            field: 'il_dispute_detail_status',
            headerName: 'Status',
            flex: 1.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            headerClassName: 'custom-header-name',
            renderCell: (params) => {
                return (
                    <Typography sx={{ ...customized_style.itemDatagrid_Typography, fontSize: 11 }}>
                        {typeof params.value === 'string'
                            ? params.value
                            : dispute_statusMsg.find((option) => option.id === params.value)?.content || ''}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                const value = dispute_statusMsg.find((option) => option.id === params.value);
                return (
                    <CustomAutocompleteCell
                        params={params}
                        options={dispute_statusMsg}
                        value={value}
                        shouldFocus={true}
                    />
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            flex: 0.3,
            cellClassName: 'actions',
            getActions: (params) => {
                return [
                    <Tooltip title="Delete">
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete"
                            onClick={handleDisputeDelete(params.row.il_dispute_id)}
                            color="inherit"
                        />
                    </Tooltip>,
                ];
            },
        },
    ];
};
